import React, { FormEventHandler, useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Dialog, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { Order } from '@luxuryescapes/contract-svc-order';

import Spinner from '~/components/Common/Spinner';
import BedbankPricingSummary from '~/components/Purchases/OrderDetail/BedbankList/BedbankPricingSummary';
import { RefundPolicy } from '~/components/Purchases/OrderDetail/BedbankList/RefundPolicies';

import {
  CHANGE_OF_MIND_WITHIN_REFUND_GUARANTEE_PERIOD,
  COLLECTION_REASONS,
  CUSTOM_REASON_SOURCE,
  CollectionReasonKey,
  EXPIRED_BNBL_PACKAGE,
  FAILED_REFUND_FROM_PAYMENT_GATEWAYS,
  FRAUD_AND_COMPLIANCE_AND_DISPUTE,
  INSURANCE_BREAKAGE,
  LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS,
  MISLEADING_CONTENT_AND_INCORRECT_PRICING_AND_CUSTOMER_RETENTION,
  PACKAGE_AMENDMENT,
  REFUND_TEST_PURCHASE,
  VARIANT_OF_REFUND_PARTIAL,
} from '~/consts/refund';

import useCurrentUser from '~/hooks/useCurrentUser';

import { cancelOrder, getOrderRefundInfo, refundStatus } from '~/services/OrdersService';
import { ReservationRate } from '~/services/ReservationService';

import asyncPoll from '~/utils/asyncPoll';
import { getLebtBusinessId } from '~/utils/businessTraveller';
import isOrderRefundable from '~/utils/isOrderRefundable';

import { CollectionReason, EnrichedRefundMetadata, RefundMetadata, RefundPayload, VendorRefundTypes } from '../types';

import ChangeOfMindWithinRefundGuaranteePeriod from './Pages/ChangeOfMindWithinRefundGuaranteePeriod';
import ExpiredBNBLPackage from './Pages/ExpiredBNBLPackage';
import FailedRefundFromPaymentGateways from './Pages/FailedRefundFromPaymentGateways';
import FraudComplianceAndDispute from './Pages/FraudComplianceAndDispute';
import InsuranceBreakage from './Pages/InsuranceBreakage';
import LateChangeOfMindBadExperienceOrUnfortunateEvents, {
  RefundTabs,
} from './Pages/LateChangeOfMindBadExperienceOrUnfortunateEvents';
import MisleadingContentIncorrectPricingAndCustomerRetention from './Pages/MisleadingContentIncorrectPricingAndCustomerRetention';
import PackageAmendment from './Pages/PackageAmendment';
import RefundConfirmation from './Pages/RefundConfirmation';
import RefundReasons from './Pages/RefundReasons';
import RefundTestPurchase from './Pages/RefundTestPurchase';
import SimpleBedbankRefundPolicies from './Pages/SimpleBedbankRefundPolicies';
import { CommonRefundFormData } from './RefundV2.types';
import {
  buildRefundPayload,
  convertStrOrBoolToBool,
  formatModalHeader,
  getCostPrice,
  getCostPriceInCostCurrency,
  getDefaultValues,
  getItemType,
  getVendorContribution,
  handleCustomFee,
  handleSelectNights,
  handleVendorPercentage,
} from './Utils/RefundUtils';
import getLPPSyntheticReservationRates from './Utils/getLPPSyntheticReservationRates';

interface Props {
  order: Partial<App.Order> | any;
  item: any;
  room: Order.BedbankItemRoom;
  customerRefundPolicy: RefundPolicy;
  supplierRefundPolicy: RefundPolicy;
  propertyTimezone: string;
  toggleRefundModal: () => void;
  roomId?: string;
  approver?: string;
  showModal: boolean;
  isItemInPaymentSchedule?: boolean;
}

export enum RefundSteps {
  Reason,
  Detail,
  Confirm,
}

interface RefundSubmitFormData {
  refundMethod: string;
  customerRefundNotification: string;
}

type ChangeOfMindWithinRefundGuaranteePeriodFormData = CommonRefundFormData;

type InsuranceBreakageFormData = CommonRefundFormData;

interface PackageAmendmentFormData extends CommonRefundFormData {
  variantOfRefund: string;
  refundAmountToCustomer: string;
  markCancelled: string;
}

interface MisleadingContentFormData extends CommonRefundFormData {
  variantOfRefund: string;
  refundAmountToCustomer: string;
  markCancelled: string;
}

interface FraudComplianceAndDisputeFormData extends CommonRefundFormData {
  markCancelled: string;
  liabilitySource: string;
}

interface FailedRefundFromPaymentGatewaysFormData extends CommonRefundFormData {
  variantOfRefund: string;
  refundAmountToCustomer: string;
  markCancelled: string;
}

type RefundTestPurchaseFormData = CommonRefundFormData;

type ExpiredBNBLPackageFormData = CommonRefundFormData;

interface LateChangeOfMindFormData extends CommonRefundFormData {
  feeOrCustom: string;
  percentageOrNumber: string;
  refundFee?: string;
  percentageRefundToCustomer?: string;
  numberOfNights?: string;
  totalRefundedToCustomer?: string;
  markCancelled: string;
  customVendorContribution?: string;
}

function generateLateChangeOfMindPayload(
  formData: LateChangeOfMindFormData,
  refundPayload: RefundPayload,
  refundMetadata: RefundMetadata,
  selectedNights?: Set<string>,
) {
  const {
    comment,
    caseId,
    feeOrCustom,
    percentageOrNumber,
    refundFee,
    markCancelled,
    // percentage values
    percentageRefundToCustomer,
    customVendorContribution,
    // select nights values
    numberOfNights,
    totalRefundedToCustomer,
    refundSubtype,
  } = formData;

  let updatedPayload = {
    ...refundPayload,
    ticket_id: caseId,
    comment,
  };

  if (parseInt(feeOrCustom) === RefundTabs.FULL_REFUND_WITH_FEE) {
    // full refund with fee
    updatedPayload = handleCustomFee({
      inputPayload: updatedPayload,
      refundFee,
      refundMetadata,
      markCancelled,
      shouldSurchargeBeRefunded: 'false',
    });
  } else {
    // custom refund
    updatedPayload.variant_of_refund = VARIANT_OF_REFUND_PARTIAL;
    updatedPayload.accounting_metadata[0].source = CUSTOM_REASON_SOURCE;
    if (percentageOrNumber === VendorRefundTypes.percentage && !selectedNights?.size) {
      // percentage
      updatedPayload = handleVendorPercentage({
        inputPayload: updatedPayload,
        customVendorContribution,
        percentageRefundToCustomer,
        refundMetadata,
      });
    } else {
      // select nights
      updatedPayload = handleSelectNights({
        inputPayload: updatedPayload,
        totalRefundedToCustomer,
        numberOfNights,
      });
    }
  }

  if (refundSubtype) {
    updatedPayload.additional_info.refund_subtype = refundSubtype;
  }

  updatedPayload.mark_cancelled = convertStrOrBoolToBool(markCancelled);
  return updatedPayload;
}

const SidebarToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '50%',
  width: 40,
  height: 40,
  zIndex: 1200,
  transition: 'right 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const SidebarDrawer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  position: 'absolute',
  top: 0,
  left: '100%',
  height: '100%',
  width: isOpen ? 300 : 0,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  borderLeft: `1px solid ${theme.palette.divider}`,
  transition: 'width 0.3s ease',
  overflow: 'hidden',
  boxShadow: theme.shadows[1],
}));

const SidebarContent = styled(Box)({
  width: 300,
  height: '100%',
  overflow: 'auto',
});

export default function LPPRefundV2({
  order,
  item,
  room,
  customerRefundPolicy,
  supplierRefundPolicy,
  propertyTimezone,
  toggleRefundModal,
  roomId = undefined,
  approver = '',
  showModal,
  isItemInPaymentSchedule = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const user = useCurrentUser();
  const [refundPayload, setRefundPayload] = useState<RefundPayload>();
  const [step, setStep] = useState(RefundSteps.Reason);
  const [isLoading, setIsLoading] = useState(false);
  const [refundMetadata, setRefundMetadata] = useState<EnrichedRefundMetadata>(undefined);
  const [reasonInfo, setReasonInfo] = useState<CollectionReason>();
  const [vendorContribution, setVendorContribution] = useState(0);
  // its objecting to LE but lE looks worse
  // eslint-disable-next-line react/hook-use-state
  const [LEProfit, setLEProfit] = useState(0);
  // eslint-disable-next-line react/hook-use-state
  const [LEContribution, setLEContribution] = useState(0);
  const isAccommodationRefundable = isOrderRefundable(order, {});
  const customerName = order.customer_full_name ?? `${order.customer_given_name} ${order.customer_surname}`;
  const currentUserName = user.user.fullName;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedNights, setSelectedNights] = useState<Set<string>>(new Set());
  const [selectedSurchargeTypes, setSelectedSurchargeTypes] = useState<Array<string>>([]);
  const [reservationRates, setReservationRates] = useState<Array<ReservationRate> | []>([]);
  const [currentCustomFeeForAccounting, setCurrentCustomFeeForAccounting] = useState<number>(0);

  const [sidebarOpen, setSidebarOpen] = useState(true);

  function updateVendorContribution(finalRefundAmount: number, updatedVendorContribution: number) {
    const roundedVendorContribution = Number(updatedVendorContribution.toFixed(2));
    const difference = finalRefundAmount - roundedVendorContribution;

    setVendorContribution(roundedVendorContribution);
    if (difference >= 0) {
      setLEContribution(Number(difference.toFixed(2)));
      setLEProfit(0);
    } else {
      setLEProfit(Number(Math.abs(difference).toFixed(2)));
      setLEContribution(0);
    }
  }

  // Handler for manual vendor-contribution edits (preserves custom value in payload)
  const handleCustomVendorContribution = useCallback(
    (newValue: string) => {
      const vendorAmountInCostCurrency = parseFloat(newValue || '0');
      const refundAmount = refundPayload?.amount || 0;

      // Check if currencies differ and convert from cost to sell currency
      const costCurrencyCode = refundMetadata?.package_cost_price_data?.cost_price_currency || '';
      const currenciesDiffer = Boolean(
        costCurrencyCode && order.currency_code && costCurrencyCode !== order.currency_code,
      );

      let vendorAmountInSellCurrency = vendorAmountInCostCurrency;
      if (currenciesDiffer) {
        const sellCostPrice = getCostPrice(refundMetadata);
        const costCostPrice = getCostPriceInCostCurrency(refundMetadata);
        const fxRatio = sellCostPrice > 0 ? costCostPrice / sellCostPrice : 1;

        // Convert from cost currency to sell currency
        if (fxRatio > 0) {
          vendorAmountInSellCurrency = vendorAmountInCostCurrency / fxRatio;
        }
      }

      // For custom vendor contribution, use customer refund amount instead of total refund amount
      const customerRefundAmount = refundPayload?.accounting_metadata?.[0]?.cash_amount || refundAmount;

      // Update the contribution split using sell currency amount
      updateVendorContribution(customerRefundAmount, vendorAmountInSellCurrency);

      // Update the refund payload with both sell and cost currency amounts
      setRefundPayload((prev) => ({
        ...prev!,
        additional_info: {
          ...prev!.additional_info,
          vendor_contribution: vendorAmountInSellCurrency,
          // Store cost currency amount if currencies differ
          ...(currenciesDiffer ? { vendor_contribution_cost_currency: vendorAmountInCostCurrency } : {}),
        },
      }));
    },
    [refundPayload?.amount, refundMetadata, order.currency_code],
  );

  const handleReasonSubmission = useCallback(
    async (selectedReason: CollectionReasonKey) => {
      setCurrentCustomFeeForAccounting(0);
      setIsLoading(true);
      const refundMetadataResponse = await getOrderRefundInfo({
        orderId: order.id_orders,
        itemId: item.id,
        roomId: roomId,
        enriched: true,
        approver: approver,
        selectedReasonKey: selectedReason,
        businessId: getLebtBusinessId(order),
      });

      // update the default
      setReasonInfo(COLLECTION_REASONS[selectedReason]);
      setRefundMetadata(refundMetadataResponse);
      const defaultPayload = getDefaultValues(refundMetadataResponse, selectedReason, currentUserName, approver);
      setRefundPayload(defaultPayload);
      // set a default vendor contribution amount
      const updatedVendorContribution = getVendorContribution(refundMetadataResponse, defaultPayload, item);
      updateVendorContribution(defaultPayload.amount, updatedVendorContribution.vendorContribution);

      // For LPP (bedbank) there is no reservation rates API; synthesize rates for Select Nights
      if (
        COLLECTION_REASONS[selectedReason].key === LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS &&
        item?.check_in &&
        item?.check_out &&
        room
      ) {
        const syntheticRates = getLPPSyntheticReservationRates(item, room, order.currency_code);
        setReservationRates(syntheticRates);
      } else {
        setReservationRates([]);
      }
      setStep(step + 1);
      setIsLoading(false);
    },
    [order.id_orders, order.currency_code, item, roomId, approver, currentUserName, room, step],
  );

  const handleClose = useCallback(() => {
    const confirmedClose = window.confirm('Are you sure? All data will be reset');
    if (confirmedClose) {
      setCurrentCustomFeeForAccounting(0);
      setStep(RefundSteps.Reason);
      setRefundMetadata(undefined);
      setRefundPayload(undefined);
      toggleRefundModal();
    }
  }, [toggleRefundModal]);

  const incrementStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const decrementStep = useCallback(() => {
    setStep(step - 1);
  }, [step]);

  const handleChangeOfMindWithinRefundGuaranteePeriodSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { caseId, comment, refundSubtype } = Object.fromEntries([
        ...formData,
      ]) as unknown as ChangeOfMindWithinRefundGuaranteePeriodFormData;

      const updatedPayload = {
        ...refundPayload,
        ticket_id: caseId,
        comment: comment,
      };

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);
      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleInsuranceBreakageSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { caseId, comment, refundSubtype } = Object.fromEntries([
        ...formData,
      ]) as unknown as InsuranceBreakageFormData;

      const updatedPayload = {
        ...refundPayload,
        ticket_id: caseId,
        comment: comment,
        amount: 0,
        merchant_fee_amount: 0,
      };

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }

      updatedPayload.accounting_metadata[0].cash_amount = 0;
      setRefundPayload(updatedPayload);
      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handlePackageAmendmentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { refundAmountToCustomer, variantOfRefund, markCancelled, caseId, comment, refundSubtype } =
        Object.fromEntries([...formData]) as unknown as PackageAmendmentFormData;

      const updatedPayload: RefundPayload = {
        ...refundPayload,
        amount: refundAmountToCustomer ? parseFloat(refundAmountToCustomer) : refundPayload.amount,
        variant_of_refund: variantOfRefund,
        mark_cancelled: markCancelled === 'true',
        ticket_id: caseId,
        comment,
        update_vcc_balance: true,
      };

      updatedPayload.accounting_metadata[0].cash_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].cash_amount;
      updatedPayload.accounting_metadata[0].accounting_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].accounting_amount;

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);

      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleMisleadingContentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      const formData = new FormData(e.currentTarget);
      const { refundAmountToCustomer, variantOfRefund, markCancelled, caseId, comment, refundSubtype } =
        Object.fromEntries([...formData]) as unknown as MisleadingContentFormData;

      const updatedPayload: RefundPayload = {
        ...refundPayload,
        amount: refundAmountToCustomer ? parseFloat(refundAmountToCustomer) : refundPayload.amount,
        variant_of_refund: variantOfRefund,
        mark_cancelled: markCancelled === 'true',
        ticket_id: caseId,
        comment,
        update_vcc_balance: true,
      };

      updatedPayload.accounting_metadata[0].cash_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].cash_amount;
      updatedPayload.accounting_metadata[0].accounting_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].accounting_amount;

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);

      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleFraudComplianceAndDisputeSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { comment, caseId, markCancelled, liabilitySource, refundSubtype } = Object.fromEntries([
        ...formData,
      ]) as unknown as FraudComplianceAndDisputeFormData;

      const updatedPayload: RefundPayload = {
        ...refundPayload,
        mark_cancelled: markCancelled === 'true',
        ticket_id: caseId,
        comment,
      };

      updatedPayload.accounting_metadata[0].source = liabilitySource;

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);
      incrementStep();
    },
    [incrementStep, refundPayload, setRefundPayload],
  );

  const handleFailedRefundFromPaymentGatewaysSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { refundAmountToCustomer, variantOfRefund, markCancelled, caseId, comment, refundSubtype } =
        Object.fromEntries([...formData]) as unknown as FailedRefundFromPaymentGatewaysFormData;

      const updatedPayload: RefundPayload = {
        ...refundPayload,
        amount: refundAmountToCustomer ? parseFloat(refundAmountToCustomer) : refundPayload.amount,
        variant_of_refund: variantOfRefund,
        mark_cancelled: markCancelled === 'true',
        ticket_id: caseId,
        comment,
        update_vcc_balance: true,
      };

      updatedPayload.accounting_metadata[0].cash_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].cash_amount;
      updatedPayload.accounting_metadata[0].accounting_amount = refundAmountToCustomer
        ? parseFloat(refundAmountToCustomer)
        : updatedPayload.accounting_metadata[0].accounting_amount;
      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);

      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleRefundTestPurchaseSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { comment, caseId, refundSubtype } = Object.fromEntries([
        ...formData,
      ]) as unknown as RefundTestPurchaseFormData;

      const updatedPayload = {
        ...refundPayload,
        ticket_id: caseId,
        comment: comment,
      };

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }
      setRefundPayload(updatedPayload);
      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleExpiredBNBLPackageSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { caseId, comment, refundSubtype } = Object.fromEntries([
        ...formData,
      ]) as unknown as ExpiredBNBLPackageFormData;

      const updatedPayload = {
        ...refundPayload,
        ticket_id: caseId,
        comment: comment,
      };

      if (refundSubtype) {
        updatedPayload.additional_info = {
          refund_subtype: refundSubtype,
        };
      }

      setRefundPayload(updatedPayload);
      const updatedVendorContribution = getVendorContribution(refundMetadata, updatedPayload, item);
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload],
  );

  const handleLateChangeOfMindSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const formObject = Object.fromEntries([...formData]) as unknown as LateChangeOfMindFormData;
      const isFullRefundWithFee = parseInt(formObject.feeOrCustom) === RefundTabs.FULL_REFUND_WITH_FEE;
      setCurrentCustomFeeForAccounting(isFullRefundWithFee ? parseFloat(formObject.refundFee ?? '0') || 0 : 0);
      const updatedPayload = generateLateChangeOfMindPayload(formObject, refundPayload, refundMetadata, selectedNights);
      setRefundPayload(updatedPayload);
      const updatedVendorContribution = getVendorContribution(
        refundMetadata,
        updatedPayload,
        item,
        formObject.totalRefundedToCustomer,
        selectedNights,
        undefined,
        true,
      );
      updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      incrementStep();
    },
    [incrementStep, item, refundMetadata, refundPayload, selectedNights],
  );

  const handleRefundSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const { refundMethod, customerRefundNotification } = Object.fromEntries([
        ...formData,
      ]) as unknown as RefundSubmitFormData;
      const updatedPayload = {
        ...refundPayload,
        refund_method: refundMethod,
        send_customer_refund_notification: customerRefundNotification,
      };

      const mappedRefund = buildRefundPayload(updatedPayload, refundMetadata, undefined, {
        orderCurrencyCode: order.currency_code,
        vendorContribution,
        currentCustomFeeForAccounting,
      });

      try {
        setIsSubmitting(true);
        await cancelOrder(order.id_orders, item.id, mappedRefund, getLebtBusinessId(order));

        try {
          await asyncPoll({
            apiCall: () => refundStatus(order.id_orders, item.id),
            validateFunction: async ({ jobState }) => {
              return jobState === undefined;
            },
            maxTime: 30000,
          });
        } catch (e) {
          // We need to continue process whether refund checking request failed or not
        } finally {
          location.reload();
        }
      } catch (err) {
        enqueueSnackbar(`Failed to process refund: ${err.message}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.id, order.id_orders, refundPayload, toggleRefundModal, currentCustomFeeForAccounting],
  );

  const handleLateChangeOfMindFieldChange = useCallback(
    (formData, selectNights, preferStoredPercentage) => {
      // try and update the refund payload, if it hits any errors we are assuming its because we don't have enough information
      try {
        const formObject = Object.fromEntries([...formData]) as unknown as LateChangeOfMindFormData;
        const updatedPayload = generateLateChangeOfMindPayload(
          formObject,
          refundPayload,
          refundMetadata,
          selectedNights,
        );

        let customerRefundAmount: undefined | string = undefined;
        // we don't want agents to be able to set vendor contribution higher than customer refund amount
        // this only works for percentage though, ignore it for nights
        if (formObject.percentageOrNumber === VendorRefundTypes.percentage) {
          customerRefundAmount = formObject.percentageRefundToCustomer;
        }

        if (selectedNights.size) {
          customerRefundAmount = formObject.totalRefundedToCustomer;
        }

        const updatedVendorContribution = getVendorContribution(
          refundMetadata,
          updatedPayload,
          item,
          customerRefundAmount,
          selectedNights || selectNights,
          undefined,
          preferStoredPercentage,
        );
        updateVendorContribution(updatedPayload.amount, updatedVendorContribution.vendorContribution);
      } catch (e) {
        // assume this means not enough of the form has been filled out
        // to successfully generate this value e.g comment, caseId
        // but we don't want this to show to users
        console.warn('Could not update form', e);
      }
    },
    [item, refundMetadata, refundPayload, selectedNights],
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const hasCompletedExperiences = useMemo(() => {
    return order.experience_items.some((experience) => experience.status === 'completed');
  }, [order.experience_items]);

  return (
    <Dialog
      open={showModal}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'visible',
        },
      }}
    >
      <SidebarToggleButton onClick={toggleSidebar} sx={{ right: sidebarOpen ? -360 : -60 }}>
        {sidebarOpen ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </SidebarToggleButton>

      <DialogTitle>{formatModalHeader(step, refundMetadata)}</DialogTitle>

      {/* Main Content */}
      {!isLoading && step === RefundSteps.Reason && (
        <RefundReasons
          itemType={getItemType(item)}
          handleReasonSubmission={handleReasonSubmission}
          isRefundable={isAccommodationRefundable}
          order={order}
          isItemInPaymentSchedule={isItemInPaymentSchedule}
          isBNBL={false}
        />
      )}
      {isLoading && <Spinner />}
      {!isLoading && step === RefundSteps.Detail && refundPayload && (
        <>
          {refundPayload?.reason === CHANGE_OF_MIND_WITHIN_REFUND_GUARANTEE_PERIOD && (
            <ChangeOfMindWithinRefundGuaranteePeriod
              decrementStep={decrementStep}
              onSubmit={handleChangeOfMindWithinRefundGuaranteePeriodSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === PACKAGE_AMENDMENT && (
            <PackageAmendment
              decrementStep={decrementStep}
              onSubmit={handlePackageAmendmentSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
              disablePartialRefund={isItemInPaymentSchedule}
            />
          )}
          {refundPayload?.reason === MISLEADING_CONTENT_AND_INCORRECT_PRICING_AND_CUSTOMER_RETENTION && (
            <MisleadingContentIncorrectPricingAndCustomerRetention
              decrementStep={decrementStep}
              onSubmit={handleMisleadingContentSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS && (
            <LateChangeOfMindBadExperienceOrUnfortunateEvents
              refundMetadata={refundMetadata}
              decrementStep={decrementStep}
              onSubmit={handleLateChangeOfMindSubmit}
              reason={reasonInfo}
              checkIn={item.check_in}
              checkOut={item.check_out}
              currencyCode={order.currency_code}
              vendorContribution={vendorContribution}
              LEContribution={LEContribution}
              handleLateChangeOfMindFieldChange={handleLateChangeOfMindFieldChange}
              handleCustomVendorContribution={handleCustomVendorContribution}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
              selectedNights={selectedNights}
              onSelectedNightsChange={setSelectedNights}
              selectedSurchargeTypes={selectedSurchargeTypes}
              onSelectedSurchargeTypesChange={setSelectedSurchargeTypes}
              reservationRates={reservationRates}
              itemId={item.id}
            />
          )}
          {refundPayload?.reason === FRAUD_AND_COMPLIANCE_AND_DISPUTE && (
            <FraudComplianceAndDispute
              decrementStep={decrementStep}
              onSubmit={handleFraudComplianceAndDisputeSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === FAILED_REFUND_FROM_PAYMENT_GATEWAYS && (
            <FailedRefundFromPaymentGateways
              decrementStep={decrementStep}
              onSubmit={handleFailedRefundFromPaymentGatewaysSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              currencyCode={refundMetadata.currency_code}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === REFUND_TEST_PURCHASE && (
            <RefundTestPurchase
              decrementStep={decrementStep}
              onSubmit={handleRefundTestPurchaseSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === EXPIRED_BNBL_PACKAGE && (
            <ExpiredBNBLPackage
              decrementStep={decrementStep}
              onSubmit={handleExpiredBNBLPackageSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              currencyCode={refundMetadata.currency_code}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
          {refundPayload?.reason === INSURANCE_BREAKAGE && (
            <InsuranceBreakage
              decrementStep={decrementStep}
              onSubmit={handleInsuranceBreakageSubmit}
              reason={reasonInfo}
              defaultComment={refundPayload.comment}
              defaultCaseId={refundPayload.ticket_id}
            />
          )}
        </>
      )}
      {!isLoading && step === RefundSteps.Confirm && (
        <RefundConfirmation
          currencyCode={order.currency_code}
          refundPayload={refundPayload}
          vendorContribution={vendorContribution}
          LEContribution={LEContribution}
          LEProfit={LEProfit}
          onSubmit={handleRefundSubmit}
          decrementStep={decrementStep}
          refundMethods={reasonInfo.defaults.methods}
          order={order}
          customerName={customerName}
          defaultCustomerRefundNotification={reasonInfo.defaults.send_customer_refund_notification}
          isSubmitting={isSubmitting}
          refundMetadata={refundMetadata}
          hasCompletedExperiences={hasCompletedExperiences}
          currentCustomFeeForAccounting={currentCustomFeeForAccounting}
        />
      )}

      {/* Sidebar Drawer */}
      <SidebarDrawer isOpen={sidebarOpen}>
        <SidebarContent>
          <Stack spacing={1} sx={{ p: 2 }}>
            <Typography>
              <Typography component="span" fontWeight="bold">
                Booking Number:
              </Typography>{' '}
              {item.booking_number}
            </Typography>
            <BedbankPricingSummary room={room} currencyCode={order.currency_code} order={order} orderItemId={item.id} />
            <SimpleBedbankRefundPolicies
              room={room}
              customerRefundPolicy={customerRefundPolicy}
              supplierRefundPolicy={supplierRefundPolicy}
              propertyTimezone={propertyTimezone}
              item={item}
            />
          </Stack>
        </SidebarContent>
      </SidebarDrawer>
    </Dialog>
  );
}
